'use client';

import { normalizeProductQuantity } from '@eliadmin/core/src/utils/product-utils';
import { CacheKey } from '@eliadmin/core/src/api/constants';
import { fetchCurrencies } from '@eliadmin/core/src/api/general/general-api';
import type { ProductDetailData } from '@eliadmin/core/src/api/products/types/product-types';
import { fetchSlugDetail } from '@eliadmin/core/src/api/slugs/slugs-api';
import type { ProductDisplayData as Product } from '@eliadmin/core/src/api/products/types/product-types';
import { trackEcommerce } from '@eliadmin/core/src/components/analytics/analytics-helpers';
import { AddToCartDialog } from '@eliadmin/core/src/components/dialog/lazy-dialogs';
import { AddToCartVariantDialog } from '@eliadmin/core/src/components/dialog/lazy-dialogs';
import type { ButtonProps } from '@eliadmin/core/src/components/ui/button';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { useDialogStore } from '@eliadmin/core/src/components/ui/dialog';
import { useAddToCart } from '@eliadmin/core/src/hooks/cart/use-add-to-cart';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import type { SelectedServices } from '@eliadmin/core/src/stores/variant-store';
import { VariantsProvider, useVariantsStore } from '@eliadmin/core/src/stores/variant-store';
import { isServer } from '@eliadmin/core/src/utils/misc-utils';
import { getProductPriceDisplay } from '@eliadmin/core/src/utils/product-price-utils';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { FiShoppingBag } from 'react-icons/fi';

type AddToCartButtonProps = ButtonProps & {
  product: ProductDetailData | Product;
  quantity?: number;
  className?: string;
  showIcon?: boolean;
  sellable?: boolean;
  customText?: string;
  type?: string;
};
export function AddToCartButton({
  className,
  showIcon,
  sellable,
  product,
  customText,
  quantity = 1,
  disabled: disabledProp,
  ...props
}: AddToCartButtonProps) {
  const { t } = useTranslations();
  const locale = useLocaleStore(s => s.currentLocale);
  const openDialog = useDialogStore(s => s.open);
  const me = useCustomerStore(s => s.me);
  const isCustomerInitialized = useCustomerStore(s => s.isInitialized);

  const currencySymbol = useGlobalStore.getState().generalLayout.currency;
  const [currencyISO, setCurrencyISO] = useState<string>('EUR');

  const { data: currencies } = useQuery({
    queryKey: [CacheKey.CURRENCIES],
    queryFn: () => fetchCurrencies({ locale }),
  });

  useEffect(() => {
    if (currencies) {
      setCurrencyISO(
        currencies?.data.find(currency => currency.symbol === currencySymbol)?.id || currencySymbol,
      );
    }
  }, [currencies, currencySymbol]);

  const addToCartMutation = useAddToCart();

  const selectedVariant = useVariantsStore(state => state.selectedVariant);
  const selectedServices = useVariantsStore(state => state.selectedServices);
  const selectedPackages = useVariantsStore(state => state.selectedPackages);
  const selectedValues = useVariantsStore(state => state.selectedValues);
  const selectedQuantity = useVariantsStore(state => state.selectedQuantity);

  const productQuery = useQuery({
    queryKey: [CacheKey.PRODUCT_DETAIL, locale, product.id, me?.authToken ?? null],
    queryFn: () =>
      fetchSlugDetail({ slug: product.slug, locale, authToken: me?.authToken })
        .then(res => res['App\\Models\\Product'])
        .catch(() => null),
    enabled: false,
  });

  const sortedSuppliers = [...(product.suppliers ?? [])].sort((a, b) => a.position - b.position);

  const firstSupplier = sortedSuppliers[0];
  const isProductEnded = product.status === 'ended';
  const hasUnresolvedVariants =
    (!!product.has_variants || !!product.variants?.length) && !selectedVariant;
  const priceDisplay = getProductPriceDisplay(selectedVariant ?? product);
  const priceOnRequest = !hasUnresolvedVariants && priceDisplay.priceOnRequest;

  const handleAddToCart = async () => {
    if (!isCustomerInitialized || priceOnRequest || (!sellable && quantity < 1)) return;
    addProductToAnalytics(product, quantity);

    const fullProduct = product.has_variants
      ? await productQuery.refetch().then(res => res.data)
      : product;

    if (fullProduct?.variants?.length) {
      openDialog({
        id: AddToCartVariantDialog.id,
        content: (
          <VariantsProvider
            key={`product-${fullProduct.id ?? fullProduct.slug}`}
            state={{
              product: fullProduct,
              selectedPackages,
              selectedVariant,
              selectedQuantity,
              selectedServices,
              selectedValues,
            }}
          >
            <AddToCartVariantDialog
              product={fullProduct}
              quantity={normalizeProductQuantity(quantity, fullProduct)}
            />
          </VariantsProvider>
        ),
      });
    } else {
      await addToCartMutation.mutateAsync({
        locale,
        // TODO: implement quantity
        body: {
          item: {
            product: { id: product.id },
            sub_items: {
              services: selectedServices
                ? selectedServices.map((selectedService: SelectedServices) => {
                    return {
                      id: selectedService.id,
                    };
                  })
                : [],
              packages: selectedPackages
                ? selectedPackages.map(selectedPackage => {
                    return {
                      id: selectedPackage.id,
                    };
                  })
                : [],
            },
          },
          qty: normalizeProductQuantity(quantity, product),
        },
      });
      trackEcommerce.addToCart({
        id: product.id.toString(),
        name: product.name,
        price: priceDisplay.currentWithVat ?? 0,
        currency: currencyISO,
      });
      openDialog({
        id: AddToCartDialog.id,
        content: <AddToCartDialog product={product} title="text631" />,
      });
    }
  };

  if (!firstSupplier?.is_affiliate && priceOnRequest) return null;

  return (
    <>
      {!firstSupplier?.is_affiliate && (
        <Button
          className={cn('space-x-1', className)}
          onClick={e => {
            e.stopPropagation();
            e.nativeEvent.preventDefault();
            handleAddToCart();
          }}
          disabled={
            !isCustomerInitialized ||
            isProductEnded ||
            priceOnRequest ||
            (disabledProp ?? !sellable)
          }
          variant={'default'}
          isLoading={addToCartMutation.isPending || productQuery.isFetching}
          {...props}
        >
          {showIcon && <FiShoppingBag className="h-5 w-5" />}
          <span
            className={cn('line-clamp-1 inline flex-1 text-ellipsis whitespace-nowrap sm:hidden')}
          >
            {customText ? t(customText) : t('text1115')}
          </span>
          <span className={cn('line-clamp-1 hidden flex-1 text-ellipsis sm:inline')}>
            {customText ? t(customText) : t('text227')}
          </span>
        </Button>
      )}
      {firstSupplier?.is_affiliate && (
        <Button
          className={cn('space-x-1', className)}
          asChild
          variant={'default'}
          isLoading={addToCartMutation.isPending || productQuery.isFetching}
          {...props}
        >
          <a href={firstSupplier.url ?? '#'}>
            {showIcon && <FiShoppingBag className="h-5 w-5" />}
            <span
              className={cn(
                'line-clamp-1 inline flex-1 text-ellipsis whitespace-nowrap text-center',
              )}
            >
              {customText ? t(customText) : t('text1226')}
            </span>
          </a>
        </Button>
      )}
    </>
  );
}

function addProductToAnalytics(product: ProductDetailData | Product, quantity: number = 1) {
  if (isServer) return;

  const category = product.categories
    ? product.categories[product.categories.length - 1]
    : undefined;

  if (window.dataLayer) {
    window.dataLayer.push({ ec: null });
    window.dataLayer.push({
      event: 'addToCart',
      ec: {
        currencyCode: useGlobalStore.getState().generalLayout.currency,
        add: {
          products: [
            {
              id: product.id,
              name: product.name,
              // brand:product.manufacturer,
              // category:product.category,
              price: getProductPriceDisplay(product).currentWithoutVat,
              quantity,
            },
          ],
        },
      },
    });
  }
}
